import { Fragment, useState, useEffect } from 'react';
import { Shield, Plus, Edit, Trash2, Save, X, Lock, AlertCircle, CheckCircle2, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { Role, ModuleName, PermissionAction, MODULE_LABELS, PERMISSION_LABELS, ModulePermission, DEFAULT_ROLES } from '../types/roles';
import { useAuditLog } from '../hooks/useAuditLog';
import { roleService } from '../services/roleService';
import { useAuth } from '../contexts/AuthContext';
import { APP_SHORTCUT_OPTIONS, AppShortcutModule, getRoleShortcutModules, setRoleShortcutModules } from '../config/appShortcuts';
import { ROUTE_PERMISSIONS } from '../config/permissions';

// Modül grupları - daha organize görünüm için
const MODULE_GROUPS: Record<string, { label: string; modules: ModuleName[] }> = {
  'general': {
    label: 'Anasayfa',
    modules: ['dashboard', 'live-tracking', 'profile']
  },
  'operations': {
    label: 'Operasyon Yönetimi',
    modules: [
      'supply-management',
      'shipment-tracking',
      'shipment-monitoring',
      'driver-tracking',
      'warehouse-operations',
      'warehouse-transfer',
      'warehouse-ramp-assignment',
      'waiting-records',
      'documents',
      'automations',
      'capacity-planning',
      'offer-requests',
      'offer-portal',
      'bid-system'
    ]
  },
  'collaboration': {
    label: 'İş Birliği Yönetimi',
    modules: [
      'collaboration-management',
      'driver-applications',
      'approved-drivers',
      'corporate-carriers',
      'corporate-external-vehicles',
      'bid-marketplace',
      'my-bids',
      'active-jobs'
    ]
  },
  'vehicles': {
    label: 'Araç Yönetimi',
    modules: ['vehicle-management', 'vehicle-documents', 'weighbridge-points', 'vehicle-inspection-forms', 'vehicle-assignment-forms', 'trailer-management', 'driver-incident-reports', 'traffic-fines', 'seyir-driver-comparison', 'shift-management', 'shift-assignments']
  },
  'personnel': {
    label: 'Personel Yönetimi',
    modules: [
      'personnel-management',
      'personnel-list',
      'logistics-applications',
      'drivers',
      'personnel-files',
      'gate-entry-exit',
      'onboarding-documents',
      'driver-leave-requests',
      'driver-payrolls'
    ]
  },
  'customer-warehouse': {
    label: 'Müşteri/Depo Yönetimi',
    modules: ['customer-management', 'warehouse-settings', 'ramp-management', 'base-prices']
  },
  'finance': {
    label: 'Finans & Masraf',
    modules: [
      'expense-submission',
      'expense-approval',
      'expense-categories',
      'accounting-workflow',
      'payment-orders',
      'cost-analysis'
    ]
  },
  'reporting': {
    label: 'Raporlar',
    modules: ['reports', 'performance-reports', 'fuel-analysis', 'return-load-potential-report', 'flexible-reports']
  },
  'communication': {
    label: 'İletişim',
    modules: ['support-center', 'help-desk-guide', 'whatsapp-send', 'whatsapp-web', 'push-notifications']
  },
  'system-definitions': {
    label: 'Sistem Tanımları',
    modules: ['supply-metadata', 'contract-management', 'api-key-management', 'share-targets', 'integrations']
  },
  'settings': {
    label: 'Genel Ayarlar',
    modules: [
      'profile',
      'user-management',
      'role-management',
      'system-logs',
      'version-history',
      'backup-management',
      'settings'
    ]
  }
};

// Mobil modül grupları - sürücü uygulaması yetkileri
const MOBIL_MODULE_GROUPS: Record<string, { label: string; modules: ModuleName[] }> = {
  'general': {
    label: 'Genel',
    modules: ['dashboard', 'profile']
  },
  'shipment': {
    label: 'Sevkiyat',
    modules: ['shipment-tracking', 'driver-tracking', 'documents']
  },
  'finance': {
    label: 'Finans',
    modules: ['expense-submission', 'payment-orders']
  },
  'bidding': {
    label: 'Teklif',
    modules: ['bid-system', 'bid-marketplace', 'my-bids', 'active-jobs']
  }
};

export function RoleManagement() {
  const { logAction } = useAuditLog();
  const { user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [permissionTab, setPermissionTab] = useState<'admin' | 'mobil'>('admin');
  const [draggedShortcut, setDraggedShortcut] = useState<AppShortcutModule | null>(null);
  const [dragSource, setDragSource] = useState<'available' | 'selected' | null>(null);
  const [dragOverSelectedIndex, setDragOverSelectedIndex] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as ModulePermission[],
    appShortcuts: [] as AppShortcutModule[],
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const loadedRoles = await roleService.getRoles();
      setRoles(loadedRoles);
    } catch (err) {
      console.error('Failed to load roles:', err);
      setError('Roller yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      appShortcuts: [],
    });
    setSelectedRole(null);
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleEdit = async (role: Role) => {
    try {
      setError('');
      const detailedRole = await roleService.getRole(role.id);
      setFormData({
        name: detailedRole.name,
        description: detailedRole.description,
        permissions: [...detailedRole.permissions],
        appShortcuts: getRoleShortcutModules(detailedRole.id, detailedRole.permissions),
      });
      setSelectedRole(detailedRole);
      setIsEditing(true);
      setIsCreating(false);
    } catch (err) {
      console.error('Failed to load role details:', err);
      setError('Rol detayları yüklenirken bir hata oluştu');
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Rol adı zorunludur');
      return;
    }

    const roleId = selectedRole?.id || `role_${Date.now()}`;

    try {
      setSaving(true);

      if (isCreating) {
        const newRole = await roleService.createRole({
          id: roleId,
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
        }, user?.id);
        setRoleShortcutModules(roleId, formData.appShortcuts);
        setSuccess('Rol başarıyla oluşturuldu');
        logAction.roleCreated(newRole.id, newRole.name);
      } else if (isEditing && selectedRole) {
        await roleService.updateRole(selectedRole.id, {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
        });
        setRoleShortcutModules(selectedRole.id, formData.appShortcuts);
        setSuccess('Rol başarıyla güncellendi');
        logAction.roleUpdated(selectedRole.id, formData.name);
      }

      await loadRoles();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (roleId: string) => {
    try {
      const role = roles.find(r => r.id === roleId);
      await roleService.deleteRole(roleId);
      setSuccess('Rol başarıyla silindi');
      await loadRoles();
      setShowDeleteConfirm(null);
      setTimeout(() => setSuccess(''), 3000);
      if (role) {
        logAction.roleDeleted(roleId, role.name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleResetToDefaults = async () => {
    if (!confirm('Tüm özel roller silinecek ve sistem rolleri varsayılana dönecek. Emin misiniz?')) {
      return;
    }

    try {
      setResetting(true);
      setError('');

      const existingRoles = await roleService.getRoles();

      for (const role of existingRoles) {
        if (!role.isSystemRole) {
          await roleService.deleteRole(role.id);
        }
      }

      for (const defaultRole of DEFAULT_ROLES) {
        const existingRole = existingRoles.find((role) => role.id === defaultRole.id);

        if (!existingRole) {
          await roleService.createRole({
            id: defaultRole.id,
            name: defaultRole.name,
            description: defaultRole.description,
            permissions: defaultRole.permissions,
          }, user?.id);
        } else {
          await roleService.updateRole(defaultRole.id, {
            name: existingRole.name,
            description: existingRole.description,
            permissions: defaultRole.permissions,
          }, {
            skipRoleUpdate: true,
          });
        }
      }

      await loadRoles();
      setSuccess('Roller varsayılan ayarlara sıfırlandı');
      setTimeout(() => setSuccess(''), 3000);
      logAction.custom({
        category: 'ROLE',
        action: 'DELETE',
        entity: 'Role',
        description: 'Tüm roller varsayılan ayarlara sıfırlandı',
        severity: 'warning',
      });
    } catch (err) {
      console.error('Failed to reset roles:', err);
      setError(err instanceof Error ? err.message : 'Roller sıfırlanırken bir hata oluştu');
    } finally {
      setResetting(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: [],
      appShortcuts: [],
    });
  };

  const toggleModulePermission = (module: ModuleName, action: PermissionAction) => {
    const moduleIndex = formData.permissions.findIndex(p => p.module === module);
    
    if (moduleIndex === -1) {
      // Modül yoksa ekle
      setFormData({
        ...formData,
        permissions: [...formData.permissions, { module, actions: [action] }],
      });
    } else {
      // Modül varsa action'ı toggle et
      const modulePermission = formData.permissions[moduleIndex];
      const hasAction = modulePermission.actions.includes(action);
      
      const updatedPermissions = [...formData.permissions];
      
      if (hasAction) {
        // Action'ı kaldır
        updatedPermissions[moduleIndex] = {
          ...modulePermission,
          actions: modulePermission.actions.filter(a => a !== action),
        };
        // Eğer hiç action kalmadıysa modülü kaldır
        if (updatedPermissions[moduleIndex].actions.length === 0) {
          updatedPermissions.splice(moduleIndex, 1);
        }
      } else {
        // Action'ı ekle
        updatedPermissions[moduleIndex] = {
          ...modulePermission,
          actions: [...modulePermission.actions, action],
        };
      }
      
      setFormData({
        ...formData,
        permissions: updatedPermissions,
      });
    }
  };

  const hasPermission = (module: ModuleName, action: PermissionAction): boolean => {
    const modulePermission = formData.permissions.find(p => p.module === module);
    return modulePermission?.actions.includes(action) || false;
  };

  const allActions: PermissionAction[] = ['create', 'read', 'update', 'delete', 'export', 'approve', 'reject'];
  const validPermissionModules = new Set<ModuleName>([
    ...(Object.values(ROUTE_PERMISSIONS).map((rule) => rule.module) as ModuleName[]),
    'collaboration-management',
    'bid-system',
  ]);
  const modules = (Object.keys(MODULE_LABELS) as ModuleName[]).filter((module) => validPermissionModules.has(module));
  const isAdminTab = permissionTab === 'admin';
  const filteredModuleGroups = Object.entries(isAdminTab ? MODULE_GROUPS : MOBIL_MODULE_GROUPS)
    .map(([groupKey, group]) => ({
      groupKey,
      label: group.label,
      modules: group.modules.filter((module, index, list) => validPermissionModules.has(module) && list.indexOf(module) === index),
    }))
    .filter((group) => group.modules.length > 0);
  const selectedShortcutOptions = formData.appShortcuts
    .map((module) => APP_SHORTCUT_OPTIONS.find((item) => item.module === module))
    .filter(Boolean);
  const availableShortcutOptions = APP_SHORTCUT_OPTIONS.filter((item) => !formData.appShortcuts.includes(item.module));

  const moveShortcutToSelected = (module: AppShortcutModule, targetIndex?: number) => {
    setFormData((current) => {
      const without = current.appShortcuts.filter((item) => item !== module);
      const next = [...without];
      if (targetIndex === undefined || targetIndex < 0 || targetIndex > next.length) {
        next.push(module);
      } else {
        next.splice(targetIndex, 0, module);
      }
      return { ...current, appShortcuts: next };
    });
  };

  const removeShortcutFromSelected = (module: AppShortcutModule) => {
    setFormData((current) => ({
      ...current,
      appShortcuts: current.appShortcuts.filter((item) => item !== module),
    }));
  };

  const reorderSelectedShortcut = (module: AppShortcutModule, targetIndex: number) => {
    setFormData((current) => {
      const list = current.appShortcuts.filter((item) => item !== module);
      list.splice(targetIndex, 0, module);
      return { ...current, appShortcuts: list };
    });
  };

  const moveSelectedShortcut = (module: AppShortcutModule, direction: 'up' | 'down') => {
    setFormData((current) => {
      const currentIndex = current.appShortcuts.indexOf(module);
      if (currentIndex === -1) return current;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= current.appShortcuts.length) return current;

      const updated = [...current.appShortcuts];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return { ...current, appShortcuts: updated };
    });
  };

  const handleShortcutDragEnd = () => {
    setDraggedShortcut(null);
    setDragSource(null);
    setDragOverSelectedIndex(null);
  };

  const setDragGhost = (event: React.DragEvent<HTMLDivElement>, title: string, group: string) => {
    const ghost = document.createElement('div');
    ghost.style.position = 'absolute';
    ghost.style.top = '-1000px';
    ghost.style.left = '-1000px';
    ghost.style.padding = '12px 16px';
    ghost.style.borderRadius = '12px';
    ghost.style.background = 'linear-gradient(135deg, #2563eb, #1d4ed8)';
    ghost.style.color = 'white';
    ghost.style.fontFamily = 'sans-serif';
    ghost.style.fontSize = '14px';
    ghost.style.fontWeight = '600';
    ghost.style.boxShadow = '0 10px 30px rgba(37, 99, 235, 0.35)';
    ghost.style.minWidth = '220px';
    ghost.innerHTML = `<div>${title}</div><div style="font-size:11px;font-weight:400;opacity:.9;margin-top:4px;">${group}</div>`;
    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, 16, 16);
    window.setTimeout(() => {
      document.body.removeChild(ghost);
    }, 0);
  };

  if (isCreating || isEditing) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isCreating ? 'Yeni Rol Oluştur' : 'Rolü Düzenle'}
            </h1>
            <p className="text-gray-600 mt-1">Rol bilgilerini ve yetkilerini tanımlayın</p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Temel Bilgiler</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rol Adı *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Örn: Muhasebe Müdürü"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Açıklama
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Rolün açıklaması"
              />
            </div>
          </div>
        </div>

        {/* Permissions Matrix */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Yetki Matrisi</h3>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setPermissionTab('admin')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${permissionTab === 'admin' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Admin
              </button>
              <button
                onClick={() => setPermissionTab('mobil')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${permissionTab === 'mobil' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Mobil
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            {isAdminTab ? 'Admin paneli modülleri için kullanıcının yapabileceği işlemleri seçin' : 'Mobil uygulama modülleri için kullanıcının yapabileceği işlemleri seçin'}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-bold text-gray-900 bg-gray-50 sticky left-0">
                    Modül
                  </th>
                  {allActions.map(action => (
                    <th key={action} className="text-center py-3 px-2 text-xs font-medium text-gray-700 bg-gray-50">
                      {PERMISSION_LABELS[action]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredModuleGroups.map((group) => (
                  <Fragment key={group.groupKey}>
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td colSpan={allActions.length + 1} className="py-3 px-4 text-sm font-bold text-blue-800 sticky left-0 bg-inherit">
                        {group.label}
                      </td>
                    </tr>
                    {group.modules.map((module, idx) => (
                      <tr key={`${group.groupKey}-${module}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 sticky left-0 bg-inherit">
                          {MODULE_LABELS[module]}
                        </td>
                        {allActions.map(action => (
                          <td key={action} className="text-center py-3 px-2">
                            <input
                              type="checkbox"
                              checked={hasPermission(module, action)}
                              onChange={() => toggleModulePermission(module, action)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Uygulamalar Bölümü</h3>
          <p className="text-sm text-gray-600 mb-6">Sol menüdeki bağlantıları sürükleyip Uygulamalar ekranına taşıyın. Sağ taraftaki sıralama dashboard kısayol sırasını belirler.</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="rounded-xl border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-900">Sol Menü Linkleri</h4>
                <p className="text-xs text-gray-500 mt-1">Sürükleyip sağ tarafa bırakın</p>
              </div>
              <div className="p-4 space-y-2 max-h-[28rem] overflow-y-auto">
                {availableShortcutOptions.map((shortcut) => (
                  <div
                    key={shortcut.module}
                    draggable
                    onDragStart={(event) => {
                      setDraggedShortcut(shortcut.module);
                      setDragSource('available');
                      setDragGhost(event, shortcut.title, shortcut.menuGroup);
                    }}
                    onDragEnd={handleShortcutDragEnd}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-3 cursor-grab active:cursor-grabbing hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{shortcut.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{shortcut.menuGroup} · {shortcut.path}</p>
                      </div>
                      <button type="button" onClick={() => moveShortcutToSelected(shortcut.module)} className="text-xs font-medium text-blue-600 hover:text-blue-700">Ekle</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`rounded-xl border border-dashed bg-blue-50/30 transition-colors ${draggedShortcut ? 'border-blue-400' : 'border-blue-300'}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (selectedShortcutOptions.length === 0) {
                  setDragOverSelectedIndex(0);
                }
              }}
              onDrop={() => {
                if (draggedShortcut) {
                  moveShortcutToSelected(draggedShortcut, dragOverSelectedIndex ?? undefined);
                  handleShortcutDragEnd();
                }
              }}
            >
              <div className="px-4 py-3 border-b border-blue-200 bg-blue-50">
                <h4 className="font-semibold text-gray-900">Uygulamalar Ekranı</h4>
                <p className="text-xs text-gray-500 mt-1">Sıralı kısayollar. Sürükleyerek yer değiştirin.</p>
              </div>
              <div className="p-4 space-y-2 min-h-[12rem] max-h-[28rem] overflow-y-auto">
                {selectedShortcutOptions.length === 0 && (
                  <div className="rounded-lg border border-dashed border-blue-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                    Buraya bırakılan linkler Uygulamalar ekranında gösterilir.
                  </div>
                )}
                {draggedShortcut && selectedShortcutOptions.length > 0 && dragOverSelectedIndex === 0 && (
                  <div className="h-2 rounded-full bg-blue-500/70" />
                )}
                {selectedShortcutOptions.map((shortcut, index) => (
                  <Fragment key={shortcut?.module}>
                    <div
                      draggable
                      onDragStart={(event) => {
                        setDraggedShortcut(shortcut!.module);
                        setDragSource('selected');
                        setDragGhost(event, shortcut!.title, shortcut!.menuGroup);
                      }}
                      onDragEnd={handleShortcutDragEnd}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverSelectedIndex(index);
                      }}
                      onDrop={() => {
                        if (draggedShortcut) {
                          reorderSelectedShortcut(draggedShortcut, index);
                          handleShortcutDragEnd();
                        }
                      }}
                      className={`rounded-lg border bg-white px-4 py-3 cursor-grab active:cursor-grabbing transition-colors ${draggedShortcut === shortcut?.module ? 'border-blue-400 opacity-60' : 'border-blue-200'} ${dragOverSelectedIndex === index ? 'shadow-[0_0_0_2px_rgba(59,130,246,0.25)]' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{index + 1}. {shortcut?.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{shortcut?.menuGroup} · {shortcut?.path}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveSelectedShortcut(shortcut!.module, 'up')} disabled={index === 0} className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Yukarı taşı">
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => moveSelectedShortcut(shortcut!.module, 'down')} disabled={index === selectedShortcutOptions.length - 1} className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Aşağı taşı">
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removeShortcutFromSelected(shortcut!.module)} className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1">Kaldır</button>
                        </div>
                      </div>
                    </div>
                    {draggedShortcut && dragOverSelectedIndex === index + 1 && (
                      <div className="h-2 rounded-full bg-blue-500/70" />
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          <button
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            İptal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rol Yönetimi</h1>
          <p className="text-gray-600 mt-1">Sistem rollerini ve yetkilerini yönetin</p>
        </div>
        <button
          onClick={handleCreateNew}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Rol
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex items-center justify-center gap-3 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span>Roller yükleniyor...</span>
        </div>
      )}

      {/* Roles Grid */}
      {!loading && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(role => (
          <div key={role.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  role.isSystemRole ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  <Shield className={`w-5 h-5 ${role.isSystemRole ? 'text-blue-600' : 'text-purple-600'}`} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{role.name}</h3>
                  {role.isSystemRole && (
                    <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                      <Lock className="w-3 h-3" />
                      Sistem Rolü (Düzenlenebilir)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEdit(role)}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Düzenle"
                >
                  <Edit className="w-4 h-4" />
                </button>
                {!role.isSystemRole && (
                  <button
                    onClick={() => setShowDeleteConfirm(role.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">{role.description}</p>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Yetki Sayısı</span>
                <span className="font-medium text-gray-900">
                  {role.permissions.length} modül
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-600">Oluşturulma</span>
                <span className="font-medium text-gray-900">
                  {new Date(role.createdAt).toLocaleDateString('tr-TR')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Rolü Sil</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Bu rolü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Evet, Sil
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset to Defaults */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-yellow-900 mb-1">Varsayılan Rollere Sıfırla</h4>
            <p className="text-sm text-yellow-700 mb-3">
              Tüm özel rolleri siler ve sistem rollerini varsayılan ayarlarına döndürür.
            </p>
            <button
              onClick={handleResetToDefaults}
              disabled={resetting}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
            >
              {resetting ? 'Sıfırlanıyor...' : 'Varsayılanlara Sıfırla'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
